import SelectComponent from "../../../components/Select";
import BlocsSettings from "./BlocsSettings";
import { usePreferences } from "../../main/state/globalState";
import {
  defaultInsuranceNetworks,
  holidayZoneLabels,
  holidayZoneSchema,
  insuranceNetworkLabels,
  insuranceNetworkValues,
  supportedHolidayZones,
} from "cadendar-shared";
import { trpc } from "../../main/components/trpc.ts";
import _ from "underts";
import HInputGroup from "../../main/components/HInputGroup";
import { useForm, useStore } from "@tanstack/react-form";
import Button from "../../main/components/Button";
import styles from "./PréférencesMainPanel.module.css";
import useOwner from "../../users/hooks/useOwner";

const itemNames = {
  débutHeureAffichage:
    "Heure à partir de laquelle les rendez vous sont affichés",
  finHeureAffichage: "Heure de fin d'affichage des rendez vous",
  duréeMinRdv: "Durée minimum d'un RDV",
  duréeDefautRdv: "Durée par défaut d'un RDV",
};

const duréeLabels = _.range(10, 65, 5).map((n) => n.toString());

const PreferencesSettingsForm = ({
  owner,
  prefs,
}: {
  owner: string;
  prefs: NonNullable<ReturnType<typeof usePreferences>>;
}) => {
  const trpcUtils = trpc.useContext();
  const editPreferencesSaveMutation =
    trpc.preference.saveEditedPrefs.useMutation({
      onSuccess: async () => {
        await trpcUtils.preference.invalidate();
        await trpcUtils.rule.getAll.invalidate();
        await trpcUtils.strategor.getPresentationSettings.invalidate();
      },
    });

  const prefsForm = useForm({
    defaultValues: {
      id: prefs.id || "",
      owner_id: prefs.owner_id || owner || "",
      duree_min_rdv: prefs.duree_min_rdv.toString() || "15",
      duree_default_rdv: prefs.duree_default_rdv.toString() || "30",
      time_division: prefs.time_division.toString() || "15",
      email_expediteur: prefs.email_expediteur || "",
      email_text: prefs.email_text || "",
      sms_name: prefs.sms_name || "",
      sms_text: prefs.sms_text || "",
      blocs_ratio: prefs.blocs_ratio?.toString() || "",
      blocs_unlocked_days: prefs.blocs_unlocked_days?.toString() || "",
      confirm_text: prefs.confirm_text || "",
      does_tp: prefs.does_tp || false,
      single_patient_file_window: prefs.single_patient_file_window ?? true,
      insurance_networks: {
        ...defaultInsuranceNetworks,
        ...prefs.insurance_networks,
      },
      holiday_zone: prefs.holiday_zone || "metropole",
    },
    onSubmit: async ({ value }) => {
      const insuranceNetworks = {
        ...defaultInsuranceNetworks,
        ...(value.insurance_networks || {}),
      };

      const prefsToSave = {
        id: value.id,
        owner_id: value.owner_id,
        duree_min_rdv: parseInt(value.duree_min_rdv),
        duree_default_rdv: parseInt(value.duree_default_rdv),
        time_division: parseInt(value.time_division),
        email_expediteur: value.email_expediteur,
        email_text: value.email_text,
        sms_name: value.sms_name,
        sms_text: value.sms_text,
        does_tp: value.does_tp,
        single_patient_file_window: value.single_patient_file_window,
        insurance_networks: value.does_tp
          ? insuranceNetworks
          : defaultInsuranceNetworks,
        holiday_zone: holidayZoneSchema.parse(value.holiday_zone),
      };
      await editPreferencesSaveMutation.mutateAsync(prefsToSave);
      prefsForm.reset(value);
    },
  });

  const doesTp = useStore(prefsForm.store, (state) => state.values.does_tp);
  return (
    <div className="rounded-md bg-white m-4 p-4 w-1/3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          prefsForm.handleSubmit();
        }}
      >
        <div className={styles.preferencesSection}>
          <div className={styles.sectionTitle}>Préférences générales</div>
          <div className={styles.sectionCard}>
            <prefsForm.Field
              name="holiday_zone"
              children={(field) => {
                return (
                  <div>
                    Zone des jours fériés :{" "}
                    <SelectComponent
                      labels={supportedHolidayZones}
                      onSelect={field.handleChange}
                      selected={field.state.value}
                      id="holidayZoneChooser"
                      className=""
                    />
                    <div className="mt-1 text-sm text-slate-600">
                      Zone sélectionnée :{" "}
                      {Object.entries(holidayZoneLabels).find(
                        ([zone]) => zone === field.state.value
                      )?.[1] || field.state.value}
                    </div>
                  </div>
                );
              }}
            />

            <prefsForm.Field
              name="duree_min_rdv"
              children={(field) => {
                return (
                  <div>
                    {itemNames["duréeMinRdv"]}:{" "}
                    <SelectComponent
                      labels={duréeLabels}
                      onSelect={field.handleChange}
                      selected={field.state.value}
                      id="minRdvLengthChooser"
                      className=""
                    />
                  </div>
                );
              }}
            />
            <prefsForm.Field
              name="duree_default_rdv"
              children={(field) => {
                return (
                  <div>
                    {itemNames["duréeDefautRdv"]}:{" "}
                    <SelectComponent
                      labels={duréeLabels}
                      onSelect={field.handleChange}
                      selected={field.state.value}
                      id="rdvDefLengthChooser"
                      className=""
                    />
                  </div>
                );
              }}
            />
            <prefsForm.Field
              name="single_patient_file_window"
              children={(field) => {
                return (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={field.state.value}
                      onChange={(event) => {
                        field.handleChange(event.target.checked);
                      }}
                    />
                    Autoriser une seule fenêtre par patient
                  </label>
                );
              }}
            />
          </div>
        </div>

        <div className={styles.preferencesSection}>
          <div className={styles.sectionTitle}>Tiers payant et réseaux</div>
          <div className={styles.sectionCard}>
            <prefsForm.Field
              name="does_tp"
              children={(field) => {
                return (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Pratiquez-vous le tiers-payant avec les mutuelles ?
                    </label>
                    <div className="flex justify-start gap-3">
                      <Button
                        title="Oui"
                        onClick={() => {
                          field.setValue(true);
                        }}
                        style={field.state.value ? "main" : "secondary"}
                      />
                      <Button
                        title="Non"
                        onClick={() => {
                          field.setValue(false);
                        }}
                        style={field.state.value ? "secondary" : "main"}
                      />
                    </div>
                  </div>
                );
              }}
            />

            {doesTp ? (
              <div className={styles.insuranceNetworksSection}>
                <div className="text-sm text-slate-700">
                  A quels réseaux appartenez-vous ?
                </div>
                <div className={styles.insuranceNetworksCard}>
                  {insuranceNetworkValues.map((networkKey) => {
                    return (
                      <div
                        key={networkKey}
                        className={styles.insuranceNetworkRow}
                      >
                        <label className={styles.insuranceNetworkCheckboxLabel}>
                          <prefsForm.Field
                            name={`insurance_networks.${networkKey}`}
                            children={(field) => {
                              return (
                                <input
                                  type="checkbox"
                                  checked={field.state.value ?? false}
                                  onChange={(e) => {
                                    field.setValue(e.target.checked);
                                  }}
                                />
                              );
                            }}
                          />
                          <span>{insuranceNetworkLabels[networkKey]}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <prefsForm.Subscribe
          selector={(state) => [
            state.isDirty,
            state.isValid,
            state.isSubmitting,
          ]}
          children={([isDirty, isValid, isSubmitting]) => {
            return (
              <div className="flex justify-start gap-3 mt-4">
                <Button
                  title="Enregistrer"
                  onClick={prefsForm.handleSubmit}
                  disabled={!isDirty || !isValid || isSubmitting}
                  style="main"
                />
                {(isDirty && (
                  <Button
                    title="Annuler"
                    onClick={() => prefsForm.reset()}
                    disabled={!isDirty}
                  />
                )) ||
                  null}
              </div>
            );
          }}
        />
      </form>
    </div>
  );
};

const PréférencesMainPanel = () => {
  const prefs = usePreferences();

  const owner = useOwner();
  const trpcUtils = trpc.useContext();
  const updateDoctorMutation = trpc.user.updateDoctorByDoctor.useMutation();

  const user = trpc.user.getConnectedUserLimited.useQuery().data;
  const doctorForm = useForm({
    defaultValues: {
      fullName: user?.fullname || "",
      address: user?.tag === "doctor" ? user.address : "",
      officePhone: user?.tag === "doctor" ? user.office_phone : "",
      speciality: user?.tag === "doctor" ? user.speciality : "",
    },
    onSubmit: async ({ value }) => {
      updateDoctorMutation.mutate({
        fullname: value.fullName,
        address: value.address,
        officePhone: value.officePhone,
        speciality: value.speciality,
      });
    },
  });

  const editPreferencesSaveMutation =
    trpc.preference.saveEditedPrefs.useMutation({
      onSuccess: async () => {
        await trpcUtils.preference.invalidate();
      },
    });

  const onSetUnlockedDaysSafe = (value: string) => {
    editPreferencesSaveMutation.mutate({
      blocs_unlocked_days: parseInt(value),
    });
  };

  const onSetRatioSafe = (value: string) => {
    editPreferencesSaveMutation.mutate({
      blocs_ratio: parseInt(value),
    });
  };

  return (
    <div className="bg-slate-200 p-2">
      <div className="rounded-md bg-white m-4 p-4 w-1/3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            doctorForm.handleSubmit();
          }}
        >
          <div>Informations communiquées aux patients</div>
          <doctorForm.Field
            name="fullName"
            children={(field) => {
              return (
                <div>
                  <HInputGroup
                    label="Nom complet"
                    id="fullNameInput"
                    value={field.state.value}
                    onChange={field.handleChange}
                    labelWidth="200px"
                    inputWidth="300px"
                  />
                </div>
              );
            }}
          />
          <doctorForm.Field
            name="address"
            children={(field) => {
              return (
                <div>
                  <HInputGroup
                    label="Adresse"
                    id="addressInput"
                    value={field.state.value}
                    onChange={field.handleChange}
                    labelWidth="200px"
                    inputWidth="300px"
                  />
                </div>
              );
            }}
          />
          <doctorForm.Field
            name="officePhone"
            children={(field) => {
              return (
                <div>
                  <HInputGroup
                    label="Téléphone"
                    id="officePhoneInput"
                    value={field.state.value}
                    onChange={field.handleChange}
                    labelWidth="200px"
                    inputWidth="300px"
                  />
                </div>
              );
            }}
          />
          <doctorForm.Field
            name="speciality"
            children={(field) => {
              return (
                <div>
                  <HInputGroup
                    label="Spécialité"
                    id="specialityInput"
                    value={field.state.value}
                    onChange={field.handleChange}
                    labelWidth="200px"
                    inputWidth="300px"
                  />
                </div>
              );
            }}
          />
          <doctorForm.Subscribe
            selector={(state) => [
              state.isDirty,
              state.isValid,
              state.isSubmitting,
            ]}
            children={([isDirty, isValid, isSubmitting]) => {
              return (
                <div className="flex justify-start gap-3 mt-4">
                  <Button
                    title="Enregistrer"
                    onClick={doctorForm.handleSubmit}
                    disabled={!isDirty || !isValid || isSubmitting}
                  />
                  {(isDirty && (
                    <Button
                      title="Annuler"
                      onClick={() => doctorForm.reset()}
                      disabled={!isDirty}
                    />
                  )) ||
                    null}
                </div>
              );
            }}
          />
        </form>
      </div>
      {prefs ? (
        <PreferencesSettingsForm owner={owner || ""} prefs={prefs} />
      ) : null}
      <BlocsSettings
        onSetUnlockedDays={onSetUnlockedDaysSafe}
        unlockedDays={prefs?.blocs_unlocked_days?.toString() || ""}
        onSetRatio={onSetRatioSafe}
        ratio={prefs?.blocs_ratio?.toString() || ""}
      />
    </div>
  );
};

export default PréférencesMainPanel;
